import type { AllTaskProperties, InstrumentForLocation } from '../../../types';
import { PropertiesType } from '../../../types';
import { supportsSlotRanges } from '../../../utils/instrumentCapabilities';
import { getRangeSize, parseSlotSpec } from '../../../utils/slots';
import { LabwareAndSourceTaskByTaskId } from '../../selectors/selectLabwareAndSourceNodeByTaskId';
import type { WorkflowSerializationError } from '../../shared/types';

type FieldType = 'start' | 'end';

type Entry = { taskId: string; field: FieldType; size: number };

function validateRangeSizes(
  taskIds: string[],
  taskPropertiesById: Record<string, AllTaskProperties>,
  instrumentsById: Record<string, InstrumentForLocation>
): WorkflowSerializationError[] {
  const errors: WorkflowSerializationError[] = [];
  const sizesByLabware: Record<
    string,
    { sizes: Set<number>; entries: Entry[] }
  > = {};

  for (const taskId of taskIds) {
    const t = taskPropertiesById[taskId];
    if (!t || t.propertiesType !== PropertiesType.Action) continue;

    const instrument =
      t.instrumentId != null ? instrumentsById[t.instrumentId] : null;
    if (!instrument || !supportsSlotRanges(instrument.driverName)) {
      continue;
    }

    const inputMap = t.labwareIdsToInputSlots ?? {};
    const outputMap = t.labwareIdsToOutputSlots ?? {};
    const labwareIds = new Set([
      ...Object.keys(inputMap),
      ...Object.keys(outputMap),
    ]);

    if (
      labwareIds.size === 0 &&
      (typeof t.taskParameters?.slot === 'string' ||
        typeof t.taskParameters?.slot === 'number')
    ) {
      labwareIds.add('__ALL__');
    }

    for (const labwareId of labwareIds) {
      const slotParam =
        typeof t.taskParameters?.slot === 'string' ||
        typeof t.taskParameters?.slot === 'number'
          ? t.taskParameters.slot
          : undefined;

      const startSpec = String(
        labwareId === '__ALL__'
          ? slotParam ?? '1'
          : inputMap[labwareId] ?? slotParam ?? '1'
      ).trim();

      const endSpec = String(
        labwareId === '__ALL__'
          ? slotParam ?? startSpec
          : outputMap[labwareId] ?? startSpec
      ).trim();

      const startSize = getRangeSize(startSpec);
      const endSize = getRangeSize(endSpec);

      sizesByLabware[labwareId] ??= { sizes: new Set<number>(), entries: [] };

      if (startSize > 1) {
        sizesByLabware[labwareId].sizes.add(startSize);
        sizesByLabware[labwareId].entries.push({
          taskId,
          field: 'start',
          size: startSize,
        });
      }
      if (endSize > 1) {
        sizesByLabware[labwareId].sizes.add(endSize);
        sizesByLabware[labwareId].entries.push({
          taskId,
          field: 'end',
          size: endSize,
        });
      }
    }
  }

  for (const [labwareId, info] of Object.entries(sizesByLabware)) {
    if (info.sizes.size <= 1) continue;
    for (const entry of info.entries) {
      errors.push({
        errorType: 'tasks',
        message: 'LABWARE_SLOT_RANGE_GLOBAL_SIZE_MISMATCH',
        taskId: entry.taskId,
        labwareId,
      });
    }
  }

  return errors;
}

function validateConsecutiveTasks(
  taskIds: string[],
  taskPropertiesById: Record<string, AllTaskProperties>,
  instrumentsById: Record<string, InstrumentForLocation>,
  labwareAndSourceTaskByTaskId: LabwareAndSourceTaskByTaskId
): WorkflowSerializationError[] {
  const errors: WorkflowSerializationError[] = [];

  for (const nextTaskId of taskIds) {
    const tNext = taskPropertiesById[nextTaskId];
    if (!tNext || tNext.propertiesType !== PropertiesType.Action) continue;

    const nextInstrument =
      tNext.instrumentId != null ? instrumentsById[tNext.instrumentId] : null;
    if (!nextInstrument || !supportsSlotRanges(nextInstrument.driverName)) {
      continue;
    }

    const pairs = labwareAndSourceTaskByTaskId[nextTaskId] ?? [];
    for (const { labwareId, sourceTaskId } of pairs) {
      if (!sourceTaskId) continue;

      const tPrev = taskPropertiesById[sourceTaskId];
      if (!tPrev || tPrev.propertiesType !== PropertiesType.Action) continue;

      const prevInstrument =
        tPrev.instrumentId != null ? instrumentsById[tPrev.instrumentId] : null;
      if (!prevInstrument || !supportsSlotRanges(prevInstrument.driverName)) {
        continue;
      }

      const prevInMap = tPrev.labwareIdsToInputSlots ?? {};
      const prevOutMap = tPrev.labwareIdsToOutputSlots ?? {};
      const nextInMap = tNext.labwareIdsToInputSlots ?? {};

      const prevSlotParam =
        typeof tPrev.taskParameters?.slot === 'string' ||
        typeof tPrev.taskParameters?.slot === 'number'
          ? tPrev.taskParameters.slot
          : undefined;
      const nextSlotParam =
        typeof tNext.taskParameters?.slot === 'string' ||
        typeof tNext.taskParameters?.slot === 'number'
          ? tNext.taskParameters.slot
          : undefined;

      const prevStartRaw = prevInMap[labwareId] ?? prevSlotParam ?? undefined;
      const prevEndRaw =
        prevOutMap[labwareId] ?? prevStartRaw ?? prevSlotParam ?? undefined;
      const nextStartRaw = nextInMap[labwareId] ?? nextSlotParam ?? undefined;

      const prevEndEff = String(prevEndRaw ?? '1').trim();
      const nextStartEff = String(nextStartRaw ?? prevEndEff).trim();

      const a = parseSlotSpec(prevEndEff);
      const b = parseSlotSpec(nextStartEff);

      const sameLength = a.length === b.length;
      const sameValues = sameLength && a.every((v, i) => v === b[i]);

      if (!sameValues) {
        errors.push({
          errorType: 'tasks',
          message: 'CONSECUTIVE_TASK_SLOT_MISMATCH',
          taskId: nextTaskId,
          labwareId,
        });
      }
    }
  }

  return errors;
}

/**
 * Validates slot range configurations for instruments that support slot ranges.
 * This includes checking for range size mismatches across labware and consecutive tasks.
 * Applies to any instrument with SlotRangeSupport capability (e.g., Hamilton, Biomek).
 */
export function getSlotRangeValidationErrors(params: {
  taskIds: string[];
  taskPropertiesById: Record<string, AllTaskProperties>;
  instrumentsById: Record<string, InstrumentForLocation>;
  labwareAndSourceTaskByTaskId: LabwareAndSourceTaskByTaskId;
}): WorkflowSerializationError[] {
  return [
    ...validateRangeSizes(
      params.taskIds,
      params.taskPropertiesById,
      params.instrumentsById
    ),
    ...validateConsecutiveTasks(
      params.taskIds,
      params.taskPropertiesById,
      params.instrumentsById,
      params.labwareAndSourceTaskByTaskId
    ),
  ];
}
